import { States } from '@/helpers/common/states';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import OrderEvent from '@/model/OrderEvent';
import OrderSite from '@/model/OrderSite';
import Page from '@/model/Page';
import React, { ReactNode, useState } from 'react';
import { Column, ResponsiveDataGrid } from '@/components/TableReponsive';
import { toCurrency } from '@/helpers/masks/toCurrency';
import { ActionProps, Dialog } from '@/components/Dialog';
import { Loading } from '@/components';
import { Container } from 'reactstrap';
import FilterVector from '@/assets/images/svg/FilterVector';
import Pagination from '@/components/Utils/Pagination';
import { FilterContentCancelTickets } from '@/features/cancelTickets/components/FilterContentCancelTickets';
import OrderItem from '@/model/OrderItem';
import { ConfirmCancelTickets } from '@/features/cancelTickets/components/ConfirmCancelTickets';
import { ReactComponent as X } from '@/assets/images/svg/x.svg';

// eslint-disable-next-line no-shadow
export enum ShouldShowModal {
  filter = 'filter',
  cancelTicket = 'cancelTicket',
}

interface Props {
  state: States;
  currentPage: Page<OrderSite, OrderSite>;
  formData: FormData;
  formErrors: FormErrors;
  title: string | ReactNode;
  visible: boolean;
  shouldShowModal: ShouldShowModal;
  onFilter: () => void;
  onChangeFormInput: OnChangeFormInput;
  clearFilter: () => void;
  onPagination: (page: number) => void;
  onShouldShowModal: ({
    value,
    newTitleModal,
  }: {
    value: ShouldShowModal;
    newTitleModal: string | React.ReactNode;
  }) => void;
  onToggle: () => void;
  cancelTicket: (orderId: string | null) => void;
}

export const CancelTicketsUI: React.FC<Props> = ({
  state,
  currentPage,
  formData,
  formErrors,
  title,
  visible,
  shouldShowModal,
  onChangeFormInput,
  onFilter,
  clearFilter,
  onPagination,
  onShouldShowModal,
  onToggle,
  cancelTicket,
}): JSX.Element => {
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const openModal = (id: string): void => {
    console.error(id);
    setSelectedOrderId(id);
    onShouldShowModal({ value: ShouldShowModal.cancelTicket, newTitleModal: 'Cancelar Pedido' });
  };

  const columns: Column<OrderSite>[] = [
    {
      key: 'id',
      header: 'Codigo do Pedido',
      width: '380px',
      sortable: true,
      render: row => row.id,
    },
    {
      key: 'itemLabel',
      header: 'Nome do Item',
      //   width: '180px',
      sortable: true,
      render: row => {
        const rawLabels =
          row.events?.flatMap((event: OrderEvent) =>
            (event.items ?? []).map((item: OrderItem) => item.itemLabel),
          ) ?? [];
        const labels = rawLabels
          .filter((l): l is string => typeof l === 'string' && l.trim() !== '')
          .map(l => l.trim());
        if (labels.length === 0) return '----';
        const uniqueLabels = Array.from(new Set(labels));
        return uniqueLabels.length === 1 ? uniqueLabels[0] : uniqueLabels.join(', ');
      },
    },
    {
      key: 'orderDate',
      header: 'Data da compra',
      width: '180px',
      align: 'center',
      render: row =>
        new Date(row.orderDate).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }),
    },
    {
      key: 'quantity',
      header: 'QTD',
      width: '180px',
      sortable: true,
      render: row => row.events.map((event: OrderEvent) => event.items.length).join(', '),
    },
    {
      key: 'startDate',
      header: 'Valor do pedido',
      width: '180px',
      sortable: true,
      render: row => toCurrency(row.totalValue),
    },

    {
      key: 'actions',
      header: 'Ações',
      width: '160px',
      align: 'center',
      render: row => (
        <X
          title="Cancelar pedido"
          className={'mr-4 svg-icon-error action-icon'}
          onClick={() => openModal(row.id)}
        />
      ),
    },
  ];

  const renderActionDialogToCancelFilter: ActionProps = {
    title: 'Limpar',
    onClick: (): void => clearFilter(),
    theme: 'noneBorder',
  };

  const renderActionDialogToCancelTicket: ActionProps = {
    title: 'Confirmar',
    onClick: (): void => cancelTicket(selectedOrderId),
    theme: 'noneBorder',
  };

  return (
    <>
      <Loading isVisible={state === States.loading} />
      <Dialog
        title={title}
        visible={visible}
        onClose={onToggle}
        footerBorder={'top'}
        position={shouldShowModal === ShouldShowModal.filter ? 'right' : 'center'}
        isContentWithCard={shouldShowModal !== ShouldShowModal.filter}
        actions={[
          {
            [ShouldShowModal.filter]: renderActionDialogToCancelFilter,
            [ShouldShowModal.cancelTicket]: { title: 'Fechar', onClick: onToggle },
          }[shouldShowModal],
          {
            [ShouldShowModal.filter]: {
              title: 'Aplicar',
              onClick: onFilter,
            },
            [ShouldShowModal.cancelTicket]: renderActionDialogToCancelTicket,
          }[shouldShowModal],
        ]}
      >
        {
          {
            [ShouldShowModal.filter]: (
              <FilterContentCancelTickets
                formData={formData}
                formErrors={formErrors}
                onChangeFormInput={onChangeFormInput}
              />
            ),
            [ShouldShowModal.cancelTicket]: <ConfirmCancelTickets />,
          }[shouldShowModal]
        }
      </Dialog>
      <Container className="mainContainer" fluid={true}>
        <div className="d-flex justify-content-between" style={{ paddingBottom: '30px' }}>
          <h5 className="pageTitle">Pedidos</h5>
          <div className="button-filter-container">
            <div className="filter-container">
              <div
                className="filter-content"
                onClick={(): void => {
                  onShouldShowModal({ value: ShouldShowModal.filter, newTitleModal: '' });
                }}
              >
                <FilterVector />
              </div>
            </div>
          </div>
        </div>
        <ResponsiveDataGrid<OrderSite>
          columns={columns}
          page={{
            list: currentPage.list,
            page: currentPage.page,
            pageSize: currentPage.pageSize,
            total: currentPage.total,
          }}
          initialSortKey="startDate"
          initialSortDir="asc"
          emptyMessage="Nenhum evento encontrado."
        />
        <Pagination
          currentPage={currentPage.page}
          totalCount={currentPage.total}
          pageSize={currentPage.pageSize}
          onPageChange={(page: number) => onPagination(page)}
          total={currentPage.total}
        />
      </Container>
    </>
  );
};
